import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import portalCaseService from '../../services/portalCaseService';
import { ArrowLeft, FileText, Download, Briefcase } from 'lucide-react';

function PortalCaseDetail() {
  const { caseId } = useParams();
  const [caseData, setCaseData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [caseRes, docsRes] = await Promise.all([
          portalCaseService.getCaseDetail(caseId),
          portalCaseService.getCaseDocuments(caseId),
        ]);
        setCaseData(caseRes.data);
        setDocuments(docsRes.data.data || []);
      } catch (err) {
        console.error('Failed to load case:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [caseId]);

  const handleDownload = async (docId, fileName) => {
    try {
      const response = await portalCaseService.downloadDocument(docId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Case not found.</p>
        <Link to="/portal" className="text-blue-600 hover:text-blue-800 text-sm mt-2 inline-block">
          Back to Dashboard
        </Link>
      </div>
    );
  }

  const statusColors = {
    active: 'bg-green-100 text-green-800',
    closed: 'bg-gray-100 text-gray-800',
    pending: 'bg-yellow-100 text-yellow-800',
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'documents', label: `Documents (${documents.length})` },
  ];

  return (
    <div>
      <Link
        to="/portal"
        className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-4"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Dashboard
      </Link>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3">
                <Briefcase className="h-5 w-5 text-gray-400" />
                <h1 className="text-xl font-bold text-gray-900">{caseData.title}</h1>
              </div>
              {caseData.case_number && (
                <p className="mt-1 text-sm text-gray-500">Case #{caseData.case_number}</p>
              )}
            </div>
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[caseData.status] || 'bg-gray-100 text-gray-800'}`}>
              {caseData.status}
            </span>
          </div>
          {caseData.attorney_name && (
            <p className="mt-3 text-sm text-gray-600">
              Attorney: <strong>{caseData.attorney_name}</strong>
            </p>
          )}
        </div>

        {/* Tabs */}
        <div className="border-t border-gray-200">
          <nav className="flex gap-0 -mb-px px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab content */}
      {activeTab === 'overview' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Case Details</h2>
          {caseData.description ? (
            <p className="text-gray-700 whitespace-pre-wrap">{caseData.description}</p>
          ) : (
            <p className="text-gray-400 italic">No description available.</p>
          )}
          <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase">Case Type</dt>
              <dd className="mt-1 text-sm text-gray-900">{caseData.case_type || 'N/A'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase">Status</dt>
              <dd className="mt-1 text-sm text-gray-900 capitalize">{caseData.status}</dd>
            </div>
          </dl>
        </div>
      )}

      {activeTab === 'documents' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {documents.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <FileText className="h-8 w-8 text-gray-300 mx-auto mb-2" />
              <p>No documents have been shared with you for this case.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {documents.map((doc) => (
                <div key={doc.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{doc.file_name}</p>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(doc.file_size)}
                        {doc.shared_by && ` · Shared by ${doc.shared_by}`}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDownload(doc.id, doc.file_name)}
                    className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 flex-shrink-0 ml-3"
                  >
                    <Download className="h-4 w-4" />
                    Download
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PortalCaseDetail;
